import { makeAutoObservable } from 'mobx';
import { Point, ElementStyle, Element, BoundingBox } from '@/types';
import { DEFAULT_ELEMENT_STYLE } from '@/utils/constants';
import { getCombinedBounds } from '@/utils/geometry';
import type { RootStore } from './RootStore';

export type SelectToolMode =
  | { type: 'idle' }
  | {
      type: 'dragging';
      startPoint: Point;
      elementIds: string[];
      initialPositions?: Map<string, Point>;
      initialPoints?: Map<string, Point[]>;
    }
  | {
      type: 'resizing';
      handle: string;
      elementId: string;
      initialElement: Element;
      startPoint: Point;
    }
  | {
      type: 'multi-resizing';
      handle: string;
      elementIds: string[];
      initialElements: { [id: string]: Element };
      initialBounds: BoundingBox;
      startPoint: Point;
    }
  | { type: 'selection-rectangle' }
  | {
      type: 'moving-control-point';
      elementId: string;
      pointIndex: number;
      workingPoints: Point[];
    };

export class UIStore {
  rootStore: RootStore | null = null;
  activeTool: string = 'select';
  isDragging: boolean = false;
  isPanning: boolean = false;
  editingTextId: string | null = null;

  // viewport
  zoom: number = 1;
  canvasOffset: { x: number; y: number } = { x: 0, y: 0 };

  // tool state
  isDrawing: boolean = false;
  previewElement: any | null = null;
  startPoint: Point | null = null;
  currentPoint: Point | null = null;
  spacebarPressed: boolean = false;
  initialElementPositions: Map<string, Point> = new Map();
  persistentStyle: ElementStyle = { ...DEFAULT_ELEMENT_STYLE };
  knotInsertionPreview: {
    position: Point;
    index: number;
    elementId: string;
  } | null = null;

  // select tool state
  selectToolMode: SelectToolMode = { type: 'idle' };

  // Canvas SVG element reference for export functionality
  canvaSvgElement: SVGSVGElement | null = null;

  constructor() {
    makeAutoObservable(this, { rootStore: false });
    this.canvasOffset = { x: 0, y: 0 };
  }

  init(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  // Actions
  setActiveTool(tool: string) {
    this.activeTool = tool;
  }

  setIsDragging(isDragging: boolean) {
    this.isDragging = isDragging;
  }

  setIsPanning(isPanning: boolean) {
    this.isPanning = isPanning;
  }

  setEditingTextId(id: string | null) {
    this.editingTextId = id;
  }

  startEditingText(id: string) {
    this.editingTextId = id;
  }

  stopEditingText() {
    this.editingTextId = null;
  }

  setZoom(zoom: number) {
    this.zoom = zoom;
    this.canvasOffset = { x: 0, y: 0 };
  }

  zoomIn() {
    this.setZoom(Math.min(5, this.zoom * 1.2));
  }

  zoomOut() {
    this.setZoom(Math.max(0.1, this.zoom / 1.2));
  }

  setOffset(offset: { x: number; y: number }) {
    this.canvasOffset = offset;
  }

  panBy(delta: { deltaX: number; deltaY: number }) {
    this.canvasOffset.x += delta.deltaX;
    this.canvasOffset.y += delta.deltaY;
  }

  resetViewport() {
    this.zoom = 1;
    this.canvasOffset = { x: 0, y: 0 };
  }

  startDrawing(point: Point) {
    this.isDrawing = true;
    this.startPoint = point;
    this.currentPoint = point;
    this.initialElementPositions.clear();
  }

  updateDrawing(point: Point) {
    if (this.isDrawing) {
      this.currentPoint = point;
    }
    this.initialElementPositions.clear();
  }

  endDrawing() {
    this.isDrawing = false;
    this.startPoint = null;
    this.currentPoint = null;
    this.initialElementPositions.clear();
  }

  setPreviewElement(element: any | null) {
    this.previewElement = element;
  }

  setKnotInsertionPreview(
    preview: { position: Point; index: number; elementId: string } | null
  ) {
    this.knotInsertionPreview = preview;
  }

  setSelectToolMode(mode: SelectToolMode) {
    this.selectToolMode = mode;
  }

  updateWorkingPoints(points: Point[]) {
    if (this.selectToolMode.type === 'moving-control-point') {
      this.selectToolMode = { ...this.selectToolMode, workingPoints: points };
    }
  }

  zoomToSelection() {
    if (!this.rootStore) return;
    const selectedElements = this.rootStore.projectStore.selectedElements;
    if (selectedElements.length === 0) return;

    const bounds = getCombinedBounds(selectedElements);
    if (!bounds) return;

    const padding = 100;
    // This assumes we have access to window, which is fine for this app
    const availableWidth = window.innerWidth - padding * 2;
    const availableHeight = window.innerHeight - padding * 2;

    const scaleX = availableWidth / bounds.width;
    const scaleY = availableHeight / bounds.height;
    const scale = Math.min(scaleX, scaleY, 5); // Max zoom 5x

    const newZoom = Math.max(0.1, scale);
    const centerX = bounds.x + bounds.width / 2;
    const centerY = bounds.y + bounds.height / 2;

    this.setZoom(newZoom);
    this.setOffset({
      x: window.innerWidth / 2 - centerX * newZoom,
      y: window.innerHeight / 2 - centerY * newZoom,
    });
  }

  setSpacebarPressed(pressed: boolean) {
    this.spacebarPressed = pressed;
  }

  setCanvasSvgElement(element: SVGSVGElement | null) {
    this.canvaSvgElement = element;
  }
}
